import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Progress,
  Divider,
  message,
  Table,
  Badge
} from 'antd';
import {
  UserOutlined,
  UserAddOutlined,
  ReloadOutlined,
  TrophyOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  LoginOutlined,
  UserDeleteOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const StudentReport = () => {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  
  const [summaryData, setSummaryData] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real API calls using axios
  const fetchSummaryData = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/report/student/summary`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        from: dateRange[0].format('YYYY-MM-DD'),
        to: dateRange[1].format('YYYY-MM-DD')
      }
    });
    return response.data;
  };

  const fetchDetailsData = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/report/student/new/details`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        from: dateRange[0].format('YYYY-MM-DD'),
        to: dateRange[1].format('YYYY-MM-DD')
      }
    });
    return response.data;
  };

  const loadData = async () => {
    if (!API_URL) {
      setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const [summary, details] = await Promise.all([
        fetchSummaryData(),
        fetchDetailsData()
      ]);
      
      setSummaryData(summary);
      setDetailsData(details);
      
      message.success('Report data loaded successfully');
    } catch (err) {
      console.error('Error loading report data:', err);
      
      let errorMessage = 'Failed to load report data';
      if (err.response) {
        // Server responded with error status
        if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.message || 'Invalid request parameters';
        } else {
          errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        // Request made but no response received
        errorMessage = 'Network error. Please check your connection.';
      } else {
        // Something else happened
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('student-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Student Analytics Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .ant-progress-outer { 
              display: inline-block !important; 
              width: 200px !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  // Prepare chart data
  const ageDistributionData = detailsData ? [
    { name: 'Under 10', value: parseInt(detailsData.age_under_10) || 0, color: '#1890ff' },
    { name: '10-12', value: parseInt(detailsData.age_10_12) || 0, color: '#52c41a' },
    { name: '13-15', value: parseInt(detailsData.age_13_15) || 0, color: '#faad14' },
    { name: '16-18', value: parseInt(detailsData.age_16_18) || 0, color: '#f5222d' },
    { name: 'Above 18', value: parseInt(detailsData.age_above_18) || 0, color: '#722ed1' }
  ].filter(item => item.value > 0) : []; // Only show age groups with data

  const loginEngagementData = detailsData ? [
    { name: 'Active Users', value: parseInt(detailsData.logged_in_count) || 0, color: '#52c41a' },
    { name: 'Never Logged In', value: parseInt(detailsData.never_logged_in) || 0, color: '#f5222d' }
  ].filter(item => item.value > 0) : [];

  const studentTypeData = summaryData ? [
    { name: 'New Students', value: parseInt(summaryData.new_students) || 0, color: '#52c41a' },
    { name: 'Repeat Students', value: parseInt(summaryData.repeat_students) || 0, color: '#1890ff' }
  ].filter(item => item.value > 0) : [];

  // Prepare top active students table data
  const topActiveStudentsColumns = [
    {
      title: 'Rank',
      key: 'rank',
      render: (_, __, index) => (
        <Badge 
          count={index + 1} 
          style={{ 
            backgroundColor: index === 0 ? '#faad14' : index === 1 ? '#d9d9d9' : index === 2 ? '#cd7f32' : '#1890ff' 
          }}
        />
      ),
      width: 60,
    },
    {
      title: 'Student',
      dataIndex: 'nickname',
      key: 'nickname',
      render: (nickname, record) => (
        <Space>
          <UserOutlined />
          <Text strong>{nickname || `Student ${record.userid}`}</Text>
        </Space>
      ),
    },
    {
      title: 'Login Count',
      dataIndex: 'login_count',
      key: 'login_count',
      render: (count) => (
        <Space>
          <LoginOutlined style={{ color: '#52c41a' }} />
          <Text strong style={{ color: '#52c41a' }}>{count}</Text>
        </Space>
      ),
      sorter: (a, b) => b.login_count - a.login_count,
    },
  ];

  const topActiveStudentsData = detailsData?.top_active_students || [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          <p className="text-blue-600">
            {`Count: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${payload[0].name}`}</p>
          <p className="text-blue-600">
            {`Count: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="student-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Student Analytics Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Student Analytics Report
          </Title>
          <Text type="secondary">
            Comprehensive overview of student enrollment, demographics, and engagement
          </Text>
        </div>

        {/* Date Range and Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Space>
              <CalendarOutlined />
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                Update Report
              </Button>
              <Button 
                type="default" 
                icon={<FilePdfOutlined />}
                onClick={handlePrintPDF}
                disabled={!summaryData && !detailsData}
              >
                Print to PDF
              </Button>
            </Space>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          {summaryData && detailsData && (
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Students"
                    value={summaryData.total_students}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="New Students"
                    value={summaryData.new_students}
                    prefix={<UserAddOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Active Users"
                    value={detailsData.logged_in_count}
                    prefix={<LoginOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Never Logged In"
                    value={detailsData.never_logged_in}
                    prefix={<UserDeleteOutlined />}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Student Type Distribution */}
            <Col xs={24} lg={12}>
              <Card 
                title="Student Type Distribution"
                extra={<TrophyOutlined />}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={studentTypeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#1890ff" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            {/* Age Distribution */}
            <Col xs={24} lg={12}>
              <Card title="Age Distribution">
                {ageDistributionData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={ageDistributionData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent, value }) => `${name} (${value})`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {ageDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="small" wrap>
                        {ageDistributionData.map((entry) => (
                          <Space key={entry.name} size="small">
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: entry.color,
                                borderRadius: '50%' 
                              }}
                            />
                            <Text style={{ fontSize: '12px' }}>{entry.name}: {entry.value}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No age distribution data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Second Row of Charts */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Login Engagement Distribution */}
            <Col xs={24} lg={12}>
              <Card title="Login Engagement">
                {loginEngagementData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={loginEngagementData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {loginEngagementData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="large">
                        {loginEngagementData.map((entry) => (
                          <Space key={entry.name}>
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: entry.color,
                                borderRadius: '50%' 
                              }}
                            />
                            <Text>{entry.name}: {entry.value}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No login engagement data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Key Insights */}
            <Col xs={24} lg={12}>
              <Card title="Key Insights & Metrics">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {summaryData && detailsData && (
                    <>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <Text>New Student Rate</Text>
                          <Text strong style={{ color: '#52c41a' }}>
                            {((summaryData.new_students / summaryData.total_students) * 100).toFixed(1)}%
                          </Text>
                        </div>
                        <Progress 
                          percent={(summaryData.new_students / summaryData.total_students) * 100} 
                          strokeColor="#52c41a"
                          showInfo={false}
                        />
                      </div>
                      
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <Text>User Engagement Rate</Text>
                          <Text strong style={{ color: '#1890ff' }}>
                            {detailsData.new_student_count > 0 
                              ? ((detailsData.logged_in_count / detailsData.new_student_count) * 100).toFixed(1)
                              : '0.0'
                            }%
                          </Text>
                        </div>
                        <Progress 
                          percent={detailsData.new_student_count > 0 
                            ? (detailsData.logged_in_count / detailsData.new_student_count) * 100 
                            : 0
                          } 
                          strokeColor="#1890ff"
                          showInfo={false}
                        />
                      </div>

                      <Divider />
                      
                      <Row gutter={16}>
                        <Col span={12}>
                          <Statistic
                            title="Revenue Potential"
                            value={(summaryData.avg_spending_per_student * summaryData.total_students)}
                            prefix="$"
                            precision={0}
                            valueStyle={{ fontSize: '18px' }}
                          />
                        </Col>
                        <Col span={12}>
                          <div>
                            <Text type="secondary">Dominant Age Group</Text>
                            <br />
                            <Text strong style={{ fontSize: '18px' }}>
                              {ageDistributionData.length > 0 
                                ? ageDistributionData.reduce((max, current) => 
                                    current.value > max.value ? current : max
                                  ).name
                                : 'N/A'
                              }
                            </Text>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Top Active Students */}
          {topActiveStudentsData.length > 0 && (
            <Card title="Top 5 Most Active Students" style={{ marginBottom: '24px' }}>
              <Table
                columns={topActiveStudentsColumns}
                dataSource={topActiveStudentsData}
                rowKey="userid"
                pagination={false}
                size="middle"
                bordered
                showHeader={true}
              />
            </Card>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default withRoleAccess(StudentReport, ['superadmin']);